import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { publicApi } from '../../api/client';
import './CartDrawer.css';

const CartDrawer = ({ isOpen, onClose, items, subtotal, updateQuantity, removeItem, itemCount, clearCart }) => {
  const { t } = useTranslation();
  const [isCheckoutFormOpen, setIsCheckoutFormOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  useEffect(() => {
    if (!showSuccessPopup) return undefined;
    const timer = setTimeout(() => {
      setShowSuccessPopup(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [showSuccessPopup]);

  if (!isOpen) return null;

  const filledSegments = Math.min(3, Math.floor(itemCount));

  return (
    <div className="cart-overlay" aria-modal="true" role="dialog">
      {showSuccessPopup && (
        <div className="order-success-popup" role="status" aria-live="polite">
          <span className="order-success-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M9.2 16.2 4.9 12l1.4-1.4 2.9 2.8 8.5-8.3L19 6.5z" />
            </svg>
          </span>
          <span>{t('cart.orderSuccess')}</span>
        </div>
      )}
      <button type="button" className="cart-overlay-backdrop" aria-label={t('cart.close')} onClick={onClose} />

      <aside className="cart-drawer">
        <header className="cart-drawer-header">
          <h2>{t('cart.title')} ({items.length} {t('cart.products')})</h2>
          <button type="button" className="cart-drawer-close" aria-label={t('cart.close')} onClick={onClose}>×</button>
        </header>

        <div className="cart-drawer-shipping">
          {itemCount >= 3 && (
            <p className="cart-drawer-shipping-text">{t('cart.freeShipping')}</p>
          )}
          <div className="cart-drawer-progress">
            {[1, 2, 3].map((segment) => (
              <div key={segment} className={`cart-drawer-progress-segment${segment <= filledSegments ? ' filled' : ''}`} />
            ))}
          </div>
        </div>

        <div className="cart-drawer-body">
          {items.length === 0 ? (
            <p style={{ fontSize: 14 }}>{t('cart.empty')}</p>
          ) : (
            items.map((item) => (
              <div key={item.id} className="cart-item">
                <div className="cart-item-image">
                  <img src={item.image} alt={item.name} loading="lazy" />
                </div>
                <div className="cart-item-info">
                  <p className="cart-item-name">{item.name}</p>
                  <p className="cart-item-meta">{item.size}</p>
                  {item.description && (
                    <p className="cart-item-meta cart-item-desc">{item.description}</p>
                  )}
                  <div className="cart-item-controls">
                    <div className="cart-item-qty">
                      <button type="button" aria-label={t('cart.decrease')} onClick={() => updateQuantity(item.id, item.quantity - 1)}>−</button>
                      <span>{item.quantity}</span>
                      <button type="button" aria-label={t('cart.increase')} onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                    </div>
                    <button type="button" className="cart-item-remove" aria-label={t('cart.remove')} onClick={() => removeItem(item.id)}>
                      {t('cart.remove')}
                    </button>
                  </div>
                </div>
                <div className="cart-item-price">
                  <span>${item.price.toFixed(2)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="cart-drawer-footer">
          <div className="cart-drawer-total-row">
            <span>{t('cart.total')}</span>
            <span className="cart-drawer-total-amount">${subtotal.toFixed(2)}</span>
          </div>

          <button type="button" className="cart-drawer-checkout-btn" onClick={() => setIsCheckoutFormOpen(true)}>
            {t('cart.checkout')}
          </button>

          {isCheckoutFormOpen && (
            <form className="cart-drawer-checkout-form" onSubmit={async (e) => {
              e.preventDefault();
              if (!items.length) {
                setSubmitError(t('cart.emptyError'));
                return;
              }

              setIsSubmitting(true);
              setSubmitError('');

              const mappedItems = items.map((item) => ({
                product_id: item.id,
                product_name: item.name,
                quantity: item.quantity,
                unit_price: Number(item.price || 0),
                line_total: Number(item.price || 0) * Number(item.quantity || 0),
              }));

              const payload = {
                full_name: customerName,
                phone: customerPhone,
                email: customerEmail,
                address: customerAddress,
                items: mappedItems,
                subtotal: Number(subtotal || 0),
                promo_code: '',
                discount: 0,
                total: Number(subtotal || 0),
                note: '',
              };

              try {
                await publicApi.post('/api/orders/', payload);
                setShowSuccessPopup(true);
                setIsCheckoutFormOpen(false);
                setCustomerName('');
                setCustomerPhone('');
                setCustomerEmail('');
                setCustomerAddress('');
                if (clearCart) clearCart();
              } catch (error) {
                setSubmitError(
                  error?.response?.data?.detail ||
                  t('cart.submitError')
                );
              } finally {
                setIsSubmitting(false);
              }
            }}>
              <div className="checkout-form-row">
                <label className="checkout-label">
                  {t('account.fullName')}
                  <input type="text" className="checkout-input" value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)} placeholder={t('cart.fullNamePlaceholder')} required />
                </label>
              </div>
              <div className="checkout-form-row">
                <label className="checkout-label">
                  {t('account.phone')}
                  <input type="tel" className="checkout-input" value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)} placeholder={t('cart.phonePlaceholder')} required />
                </label>
              </div>
              <div className="checkout-form-row">
                <label className="checkout-label">
                  {t('account.email')}
                  <input type="email" className="checkout-input" value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)} placeholder={t('cart.emailPlaceholder')} required />
                </label>
              </div>
              <div className="checkout-form-row">
                <label className="checkout-label">
                  {t('cart.address')}
                  <input type="text" className="checkout-input" value={customerAddress}
                    onChange={(e) => setCustomerAddress(e.target.value)} placeholder={t('cart.addressPlaceholder')} required />
                </label>
              </div>
              <button type="submit" className="checkout-submit-btn" disabled={isSubmitting}>
                {isSubmitting ? t('cart.submitting') : t('cart.submitOrder')}
              </button>
              {submitError && <p style={{ color: '#b91c1c', marginTop: 8 }}>{submitError}</p>}
            </form>
          )}

        </div>
      </aside>
    </div>
  );
};

export default CartDrawer;
